import React from 'react';
import { Divider, Typography } from '@mui/material';
import api from 'lib/trpcClient';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { Column } from 'components/Layout';
import SearchField from 'further-ui/components/SearchField';
import { usePaginatedFilters } from 'further-ui/hooks';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';

const pageId = 'lp-user-management-roles';

const Roles: React.FC = () => {
  const { pagination, filters, handleChangeFilters } = usePaginatedFilters(
    pageId,
    {
      defaultFilters: { searchQuery: '' },
      defaultOrder: { orderBy: 'name', order: 'asc' },
      orderByOptions: ['name', 'createdAt', 'updatedAt'],
    },
  );

  const [data, { isFetching }] = api.adminUserRole.list.useSuspenseQuery({
    searchQuery: filters.searchQuery,
    ...pagination,
  });

  const roles = data.results;

  const columns: Array<TableColumn<(typeof roles)[number]>> = [
    {
      label: 'Role name',
      key: 'name',
      render: (role) => <Typography variant="inherit">{role.name}</Typography>,
    },
    {
      label: 'Description',
      key: 'description',
      render: (role) => (
        <Typography variant="inherit" color="textSecondary">
          {role.description || '-'}
        </Typography>
      ),
    },
    {
      label: 'Permissions',
      key: 'permissions',
      render: (role) => (
        <Typography variant="inherit">
          {role.containsAllPermissions
            ? 'All permissions'
            : `${role.permissions?.length ?? 0} permissions`}
        </Typography>
      ),
    },
  ];

  return (
    <Column spacing="lg">
      <PageFilters>
        <Filter size="sm">
          <SearchField
            autoSearchOnDebounce
            debounceTime={100}
            name="searchQuery"
            placeholder="Search by role"
            fullWidth
            value={filters.searchQuery}
            onSearch={(searchQuery) => {
              handleChangeFilters({ searchQuery });
            }}
            allowEmptyQuery
          />
        </Filter>
      </PageFilters>
      <Divider />
      <Table
        columns={columns}
        rows={roles}
        isLoading={isFetching || pagination.isPaginationChanging}
        rowsTotalCount={data.totalCount}
        pagination={pagination}
        emptyMessage="No roles found"
      />
    </Column>
  );
};

export default Roles;
